import {
  Autocomplete,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { getOwnCompanyProducts } from 'lib/api/company';
import {
  createCreditLineEvaluation,
  getDecisionEngines,
} from 'lib/api/decisionEngine';
import { getBusinessRelationsForUser } from 'lib/api/users';
import { TBusinessRel } from 'lib/api/users/types';
import AlertBanner from 'lib/designSystem/AlertBanner';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { showAlert } from 'lib/functions/utils';
import React, { useState } from 'react';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { TType } from '..';

type Props = {
  type: TType;
  setOpen: (open: boolean) => void;
  onSuccess?: () => void;
};

type TRelation = TBusinessRel;

const InternalEvaluation: React.FC<Props> = ({ type, setOpen, onSuccess }) => {
  const dispatch = useDispatch();
  const [selectedRelation, setSelectedRelation] = useState<TRelation>();
  const [selectedProduct, setSelectedProduct] = useState<number>();
  const [selectedEngine, setSelectedEngine] = useState<number>();

  const { mutate, isPending } = createCreditLineEvaluation(dispatch);
  const { data: relations, isPending: isPendingRelations } =
    getBusinessRelationsForUser(dispatch);
  const { data: products, isPending: isPendingProducts } =
    getOwnCompanyProducts(dispatch);
  const { data: engines, isPending: isPendingEngines } = getDecisionEngines(
    dispatch,
    type,
  );

  const loading = useMemo(() => {
    return isPendingRelations || isPendingProducts || isPendingEngines;
  }, [isPendingRelations, isPendingProducts, isPendingEngines]);

  const disabled = useMemo(() => {
    return (
      selectedRelation === undefined ||
      selectedProduct === undefined ||
      selectedEngine === undefined ||
      isPending
    );
  }, [selectedRelation, selectedProduct, selectedEngine, isPending]);

  const handleSubmit = async () => {
    if (
      selectedRelation === undefined ||
      selectedProduct === undefined ||
      selectedEngine === undefined
    ) {
      showAlert(
        dispatch,
        'error',
        'Por favor, selecciona todos los campos requeridos.',
      );
      return;
    }
    mutate(
      {
        businessIdentifier: selectedRelation.identifier,
        productId: selectedProduct,
        decisionEngineId: selectedEngine,
      },
      {
        onSuccess: () => {
          setOpen(false);
          onSuccess?.();
          showAlert(
            dispatch,
            'success',
            'Evaluación creada correctamente. Vuelve en unos minutos para revisar los resultados.',
          );
        },
        onError: () => {
          showAlert(
            dispatch,
            'error',
            'Ha habido un error al crear la evaluación',
          );
        },
      },
    );
  };

  return (
    <>
      {loading ? (
        <DialogContent sx={{ minHeight: 300 }}>
          <CenteredCircularProgress type="dialog" />
        </DialogContent>
      ) : (
        <DialogContent>
          <Stack direction="column" spacing={2} pb={4} pt={1}>
            <Autocomplete
              options={relations || []}
              value={selectedRelation}
              getOptionLabel={(option) =>
                `${option.businessName} - ${option.identifier}`
              }
              renderInput={(params) => (
                <TextField
                  style={{ minWidth: 250 }}
                  {...params}
                  label="Selecciona el cliente a evaluar"
                />
              )}
              onChange={(_, newValue) =>
                setSelectedRelation(newValue ?? undefined)
              }
              fullWidth
            />
            <FormControl fullWidth margin="normal">
              <InputLabel id="product-label">Tipo de revisión</InputLabel>
              <Select
                id="product-select"
                labelId="product-label"
                label="Tipo de revisión"
                value={selectedProduct ?? ''}
                onChange={(e) => setSelectedProduct(Number(e.target.value))}
              >
                {products?.map((product) => (
                  <MenuItem key={product.id} value={product.id}>
                    {product.productType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel id="engine-label">
                Que revisión quieres realizar?
              </InputLabel>
              <Select
                id="engine-select"
                labelId="engine-label"
                label="Que revisión quieres realizar?"
                value={selectedEngine ?? ''}
                onChange={(e) => setSelectedEngine(Number(e.target.value))}
              >
                {engines?.map((engine) => (
                  <MenuItem key={engine.id} value={engine.id}>
                    {engine.name} (
                    {engine.engineType === 'tree'
                      ? 'Árbol de decisión'
                      : 'Matriz de decisión'}
                    )
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <AlertBanner
            severity="info"
            title="Información"
            message="La evaluación se ejecutará en segundo plano. Vuelve en unos minutos para revisar los resultados. Si la información del SII o del TGR está desactualizada, se consultará automáticamente antes de ejecutar la evaluación."
          />
        </DialogContent>
      )}
      <DialogActions>
        <CustomButton color="secondary" onClick={() => setOpen(false)}>
          Cancelar
        </CustomButton>
        <CustomButton
          disabled={disabled}
          loading={isPending}
          color="primary"
          onClick={() => handleSubmit()}
        >
          Evaluar
        </CustomButton>
      </DialogActions>
    </>
  );
};

export default InternalEvaluation;
